import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Briefcase, MapPin, Clock, Building, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminJobService from '../../services/admin/jobService';

const EditJob: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        location: '',
        employment_type: 'Full-time',
        department: '',
        short_description: '',
        full_description: '',
        responsibilities: '',
        requirements: '',
        salary: '',
        is_active: true
    });

    useEffect(() => {
        if (id) fetchJobDetails(id);
    }, [id]);

    const fetchJobDetails = async (jobId: string) => {
        setFetching(true);
        try {
            const data = await adminJobService.getJobById(jobId);
            setFormData({
                title: data.title,
                location: data.location,
                employment_type: data.employment_type,
                department: data.department,
                short_description: data.short_description,
                full_description: data.full_description,
                responsibilities: data.responsibilities || '',
                requirements: data.requirements || '',
                salary: data.salary || '',
                is_active: data.is_active
            });
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch job details');
            navigate('/admin/jobs');
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setLoading(true);
        try {
            await adminJobService.updateJob(id, formData as any);
            toast.success('Job updated successfully!');
            navigate('/admin/jobs');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update job');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <RefreshCw className="animate-spin text-primary-500" size={48} />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/admin/jobs')}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors border border-transparent hover:border-secondary-200"
                >
                    <ArrowLeft size={24} className="text-secondary-600" />
                </button>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold text-secondary-900">Edit Job</h1>
                    <p className="text-secondary-600 mt-1">Update job details for {formData.title}</p>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
                            <h2 className="text-xl font-bold text-secondary-900 flex items-center gap-2 border-b border-gray-100 pb-4">
                                <Briefcase size={20} className="text-primary-500" />
                                Job Details
                            </h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Job Title *</label>
                                    <input
                                        required
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. Senior Frontend Engineer"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Short Description *</label>
                                    <textarea
                                        required
                                        name="short_description"
                                        value={formData.short_description}
                                        onChange={handleChange}
                                        rows={2}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-none"
                                        placeholder="A brief summary for the job card"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Full Job Description *</label>
                                    <textarea
                                        required
                                        name="full_description"
                                        value={formData.full_description}
                                        onChange={handleChange}
                                        rows={8}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="Detailed description of the role and team..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Responsibilities</label>
                                    <textarea
                                        name="responsibilities"
                                        value={formData.responsibilities}
                                        onChange={handleChange}
                                        rows={5}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="List key responsibilities..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Requirements</label>
                                    <textarea
                                        name="requirements"
                                        value={formData.requirements}
                                        onChange={handleChange}
                                        rows={5}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="List skills, experience, and education..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
                            <h2 className="text-lg font-bold text-secondary-900 border-b border-gray-100 pb-4">Classification</h2>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2 flex items-center gap-2">
                                        <Building size={16} /> Department *
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. Engineering, Sales, HR"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2 flex items-center gap-2">
                                        <Clock size={16} /> Employment Type *
                                    </label>
                                    <select
                                        name="employment_type"
                                        value={formData.employment_type}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none bg-white"
                                    >
                                        <option value="Full-time">Full-time</option>
                                        <option value="Part-time">Part-time</option>
                                        <option value="Contract">Contract</option>
                                        <option value="Internship">Internship</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2 flex items-center gap-2">
                                        <MapPin size={16} /> Location *
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. Remote, Lagos, New York"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-secondary-700 mb-2">Salary Range</label>
                                    <input
                                        type="text"
                                        name="salary"
                                        value={formData.salary}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. $80k - $120k / yr"
                                    />
                                </div>

                                <div className="pt-4 flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-primary-600 border-gray-300 rounded-lg focus:ring-primary-500"
                                    />
                                    <label htmlFor="is_active" className="text-sm font-bold text-secondary-700 cursor-pointer">
                                        Listing Active
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 rounded-xl transition-all shadow-xl hover:shadow-2xl active:scale-95 flex items-center justify-center gap-2"
                            >
                                <Save size={20} />
                                {loading ? 'Updating...' : 'Update Job Opening'}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/admin/jobs')}
                                className="w-full bg-white border border-gray-300 text-secondary-700 font-bold py-4 rounded-xl hover:bg-gray-50 transition-all font-bold"
                            >
                                Cancel Changes
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditJob;
